import { useMemo, useState } from 'react';
import { Repeat, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * MonthView — Q32 Phase B
 *
 * Mini calendar grid for a single month.
 *   • Dots beneath each date — one dot per event (capped at 3, then "+N").
 *   • Tiny 🔁 badge when ANY plan on that day is recurring.
 *   • Tap a day → expands an inline agenda list for that date.
 *   • Prev / next month navigation.
 *
 * Props:
 *   itemsByDate — { "YYYY-MM-DD": [agendaRow, ...] }
 */
export default function MonthView({ itemsByDate }) {
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const monthLabel = anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const todayIso = new Date().toISOString().slice(0, 10);

  const shift = (n) => {
    setSelected(null);
    setAnchor(prev => new Date(prev.getFullYear(), prev.getMonth() + n, 1));
  };

  return (
    <div data-testid="calendar-month-view" className="space-y-3">
      <div className="flex items-center justify-between">
        <button
          data-testid="month-prev"
          onClick={() => shift(-1)}
          aria-label="Previous month"
          className="w-8 h-8 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:bg-[#F1F0EA]"
        >
          <ChevronLeft size={16} />
        </button>
        <h2 data-testid="month-label" className="text-sm font-extrabold text-[#111] tracking-tight">
          {monthLabel}
        </h2>
        <button
          data-testid="month-next"
          onClick={() => shift(1)}
          aria-label="Next month"
          className="w-8 h-8 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:bg-[#F1F0EA]"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <div key={i} className="text-[10px] font-bold text-[#9B9EA3] uppercase tracking-wider py-1">
            {d}
          </div>
        ))}
        {grid.map((cell) => {
          if (!cell) return <div key={Math.random()} className="aspect-square" />;
          const iso = cell.iso;
          const items = itemsByDate[iso] || [];
          const isToday = iso === todayIso;
          const isSelected = iso === selected;
          const isOtherMonth = !cell.current;
          const hasRecurring = items.some(r => r.is_recurring);
          return (
            <button
              key={iso}
              data-testid={`month-cell-${iso}`}
              onClick={() => setSelected(isSelected ? null : iso)}
              className={`aspect-square rounded-xl flex flex-col items-center justify-start pt-1.5 relative
                ${isSelected ? 'bg-[#B6E600] text-[#111]' :
                  isToday ? 'bg-[#B6E600]/15 text-[#3F5200]' :
                  'bg-white border border-black/[0.04] text-[#111]'}
                ${isOtherMonth ? 'opacity-30' : ''}
                active:scale-[0.96] transition-transform`}
            >
              <span className={`text-xs ${isToday || isSelected ? 'font-extrabold' : 'font-semibold'}`}>
                {cell.day}
              </span>
              {hasRecurring && (
                <span
                  data-testid={`month-recurring-${iso}`}
                  className="absolute top-0.5 right-0.5 text-[#3F5200]"
                >
                  <Repeat size={8} strokeWidth={3} />
                </span>
              )}
              <div className="flex gap-0.5 mt-auto mb-1 items-center">
                {items.slice(0, 3).map((_, i) => (
                  <span
                    key={i}
                    className={`w-1 h-1 rounded-full ${isSelected ? 'bg-[#111]' : 'bg-[#3F5200]'}`}
                  />
                ))}
                {items.length > 3 && (
                  <span className={`text-[8px] font-bold ml-0.5 ${isSelected ? 'text-[#111]' : 'text-[#3F5200]'}`}>
                    +{items.length - 3}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selected && (
        <div data-testid={`month-day-detail-${selected}`} className="bg-white rounded-2xl border border-black/[0.06] p-3 space-y-2">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9B9EA3]">
            {prettyDay(selected)}
          </p>
          {(itemsByDate[selected] || []).length === 0 ? (
            <p className="text-xs text-[#9B9EA3]">No events</p>
          ) : (
            itemsByDate[selected].map((row, i) => (
              <button
                key={`${row.plan.id}-${i}`}
                data-testid={`month-event-${row.plan.id}`}
                onClick={() => navigate(`/plan/${row.plan.id}`)}
                className="w-full flex items-center gap-2 text-left active:bg-[#F1F0EA] rounded-lg p-1"
              >
                <span className="text-[10px] font-extrabold text-[#3F5200] w-10">
                  {(row.plan.start_time || '').slice(0, 5) || '—'}
                </span>
                <span className="text-xs font-bold text-[#111] flex-1 truncate">{row.plan.title}</span>
                {row.is_recurring && <Repeat size={11} className="text-[#3F5200]" strokeWidth={2.6} />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function buildMonthGrid(anchor) {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const cells = [];
  // Leading days from prev month
  for (let i = startWeekday - 1; i >= 0; i--) {
    const day = prevMonthDays - i;
    const d = new Date(year, month - 1, day);
    cells.push({ iso: isoOf(d), day, current: false });
  }
  // Current month
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    cells.push({ iso: isoOf(d), day, current: true });
  }
  // Trailing to fill 42 cells (6 weeks)
  let nextDay = 1;
  while (cells.length < 42) {
    const d = new Date(year, month + 1, nextDay);
    cells.push({ iso: isoOf(d), day: nextDay, current: false });
    nextDay += 1;
  }
  return cells;
}

function isoOf(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function prettyDay(iso) {
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'long', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
}
